import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ChangeEvent,
  CompositionEvent,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/*
 * 基础控件。尺寸按设备（CR-082，DESIGN-SYSTEM 第 2 节）：
 * 电脑按当代后台的密度 —— 按钮、输入框 32px，成排的小控件 28px；手机按系统标准 —— 44 / 32。
 * 56px（`glove`）只给打卡按钮。外观走 index.css 的 token，浅色 / 深色自动跟着换。
 */

/**
 * 按钮。
 *
 * `size`：default 是 `touch`（电脑 32 / 手机 44）；`sm` 是 `touch-sm`（28 / 32），给表格行上的「批准」「通过」这类成排的小按钮；
 * `glove`（56px）**只给打卡按钮**（CR-082，`styles.test.ts` 盯着）。别在调用处写 `min-h-*` 覆盖 —— 那正是以前六个页面六种高度的由来。
 *
 * **`overlay`（36px）只给浮在被查看内容之上的控件**（CR-030，客户点名要的）：全屏看图时的翻页键。
 * `styles.test.ts` 里有一条断言盯着它的使用范围。
 *
 * `variant`：`danger` 是实心红底（确认删除这种不可逆的）；`dangerOutline` 是白底红字（驳回、拒绝、移出：
 * 常用但是否定的动作，和「批准」并排时不能比它还抢眼）。
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-body font-medium',
    'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-600 text-white shadow-xs hover:bg-brand-700 active:bg-brand-700',
        secondary: 'border border-rule-strong bg-surface text-ink shadow-xs hover:bg-surface-2',
        ghost: 'text-brand-600 hover:bg-brand-50',
        danger: 'bg-danger-solid text-white shadow-xs hover:brightness-110',
        dangerOutline: 'border border-rule-strong bg-surface text-danger shadow-xs hover:bg-surface-2',
      },
      size: {
        default: 'min-h-touch',
        sm: 'min-h-touch-sm rounded-md px-2.5',
        // 56px：只有打卡按钮（CR-082）
        glove: 'min-h-glove rounded-xl px-5 text-title',
        // 36px。见上面的说明 —— 只给浮在内容上的查看器控件用（CR-030）
        overlay: 'h-9 min-w-9 px-3 text-body leading-none',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'default', block: false },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean;
}

export function Button({
  variant,
  size,
  block,
  loading,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(button({ variant, size, block }), className)}
    >
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
}

/**
 * 长得像按钮的链接。只给「点了是下载文件」这类原生链接用（CR-058：登录页下载安卓 App）——
 * 站内跳转用 react-router 的 Link，不是这个。尺寸规矩和 Button 一样。
 */
export function AnchorButton({
  variant,
  size,
  block,
  className,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & VariantProps<typeof button>) {
  return (
    <a {...rest} className={cn(button({ variant, size, block }), className)}>
      {children}
    </a>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <span
      role="status"
      aria-label="loading"
      style={{ width: size, height: size, borderWidth: Math.max(2, size / 10) }}
      className="inline-block animate-spin rounded-full border-current border-t-transparent opacity-70"
    />
  );
}

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, required, children }: FieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-body font-medium text-ink-2">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-caption leading-relaxed text-ink-3">{hint}</p>}
      {error && <p className="text-caption font-medium text-danger">{error}</p>}
    </div>
  );
}

/** 输入类控件共用的外观。输入框和下拉必须长得一样，否则一行表单里高低不齐 */
const control = cva([
  'min-h-touch w-full rounded-lg border border-rule-strong bg-surface px-2.5 text-input text-ink shadow-xs',
  // 字号是 text-input：触屏上 16px —— 小于 16px 的输入框，iOS 一聚焦就把整页放大
  'pointer-coarse:px-3',
  'placeholder:text-ink-4 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/15',
  'disabled:bg-surface-2 disabled:text-ink-3',
]);

/**
 * 中文输入法收尾那一下要单独补一次 onChange：
 * 安卓 WebView 上候选词上屏只发 `compositionend`、不再发 `input` 事件，React 的 onChange
 * 拿不到最后那次值 —— 状态里还是上屏前的内容，界面上就是「输完了按钮还灰着，删一个字才亮」
 * （客户在真机上实测：英文键盘正常、中文输入法必现）。日报、备注的草稿同理会少存最后一个词。
 * 修在控件层，所有文本框一起好；handler 只读 `e.target.value`，把组合事件当 ChangeEvent 递过去是安全的。
 */
function syncAfterComposition<T extends HTMLInputElement | HTMLTextAreaElement>(
  onChange: ((e: ChangeEvent<T>) => void) | undefined,
  onCompositionEnd: ((e: CompositionEvent<T>) => void) | undefined,
) {
  return (e: CompositionEvent<T>) => {
    onCompositionEnd?.(e);
    onChange?.(e as unknown as ChangeEvent<T>);
  };
}

export function Input({ className, onChange, onCompositionEnd, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      onChange={onChange}
      onCompositionEnd={syncAfterComposition(onChange, onCompositionEnd)}
      className={cn(control(), className)}
    />
  );
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={cn(control(), className)} />;
}

/**
 * 多行输入。和 Input / Select 共用同一套外观，只是多了纵向内边距和 resize。
 *
 * `rows` 默认 3。需要更高就传 rows，别在调用处写 `h-*` ——
 * 那样在不同页面里同一种输入框会长出不同高度。
 */
export function Textarea({
  className,
  rows = 3,
  onChange,
  onCompositionEnd,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      rows={rows}
      onChange={onChange}
      onCompositionEnd={syncAfterComposition(onChange, onCompositionEnd)}
      className={cn(control(), 'resize-y py-2 leading-relaxed', className)}
    />
  );
}

/**
 * 勾选框（CR-038，职位页的权限表要用）。
 *
 * 整行是命中区：`min-h-touch` 的 label 包着原生 checkbox 和文字，点文字也算点中 ——
 * 原生勾选框本身只有 16px。`hint` 是这个勾意味着什么，直接渲染出来，不放 title（手机上没有悬停）。
 */
export function Checkbox({
  label,
  hint,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  return (
    <label
      className={cn(
        'flex min-h-touch cursor-pointer items-start gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-surface-2',
        rest.disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
        className,
      )}
    >
      <input
        type="checkbox"
        {...rest}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-rule-strong accent-brand-600 focus:ring-2 focus:ring-brand-500/20"
      />
      <span className="min-w-0">
        <span className="block text-body leading-snug text-ink">{label}</span>
        {hint && <span className="mt-0.5 block text-caption leading-relaxed text-ink-3">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * 卡片。
 *
 * `padding` 做成变体是因为以前每个调用方自己写 `p-4` / `p-5` / `px-4 py-3`，
 * 同一种卡片在六个地方有六种内边距。要特殊尺寸就传 className 覆盖，`cn` 会让后传的赢。
 *
 * CR-082 起只有描边、没有阴影：网页端的内容本身就放在一张白色大卡片里（方案 A），
 * 里面的卡片再带阴影就是一层叠一层；手机上白卡放在浅灰底上，描边已经够分开。
 */
const card = cva('rounded-xl border border-rule bg-surface', {
  variants: {
    padding: {
      none: '',
      sm: 'px-4 py-3',
      md: 'p-4',
      lg: 'p-5',
    },
  },
  defaultVariants: { padding: 'none' },
});

export function Card({
  padding,
  className,
  children,
}: VariantProps<typeof card> & { className?: string; children: ReactNode }) {
  return <div className={cn(card({ padding }), className)}>{children}</div>;
}

/** 卡片头：标题（+ 一行说明）在左，一个链接或按钮在右，下面一条线（CR-082） */
export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-rule px-4 py-3">
      <div className="min-w-0">
        <h2 className="text-title font-semibold text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-caption text-ink-3">{description}</p>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * 设置页的一段（CR-082）：左边标题 + 一句说明，右边表单，段与段之间一条线（后台常见做法）。
 * 窄屏上下叠。工地设置在用；以后的设置页照这个排，别再一张卡片从上到下堆字段。
 */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-x-8 gap-y-3 border-b border-rule px-5 py-5 last:border-b-0 max-md:px-4 max-md:py-4 md:grid-cols-[13rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,1fr)]">
      <div>
        <h2 className="text-title font-semibold text-ink">{title}</h2>
        {description && <p className="mt-1 text-caption leading-relaxed text-ink-3">{description}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'info' | 'success';
  children: ReactNode;
}) {
  const tones = {
    error: 'border-danger/30 bg-danger/8 text-danger',
    info: 'border-brand-500/30 bg-brand-50 text-brand-700',
    success: 'border-ok/30 bg-ok/8 text-ok',
  };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-lg border px-3 py-2 text-body ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-body font-semibold text-ink">{title}</p>
      {hint && <p className="max-w-xs text-body leading-relaxed text-ink-3">{hint}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/**
 * 返回（CR-044）：写上一级的名字，主色字，不画箭头（CR-081 客户不要图标）。
 *
 * **别在页面里放它** —— App 外壳由 `MobileLayout` 按路由表的 `parent` 统一画在顶栏左边，去的是固定的上一级，
 * 不是 `history.back()`：拍照后 Activity 被系统回收重开时（CR-037）历史是空的。
 * 唯一的例外是改密码页，它挂在两套外壳之外，自己按设备画。
 */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      data-action="back"
      className="-ml-1 inline-flex min-h-touch items-center px-1 text-body font-medium text-brand-600"
    >
      {label}
    </Link>
  );
}

/**
 * 页头（CR-082）：标题 + 一行说明，右边按钮（次按钮在左、主按钮在最右，只一个主按钮）。
 * 电脑上 20px；手机上是 28px 的大标题（iOS 的 large title），这一屏最重的字。
 */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 max-md:mb-4">
      <div className="min-w-0">
        <h1 className="text-heading font-semibold tracking-tight text-ink max-md:font-bold">
          {title}
        </h1>
        {description && <p className="mt-0.5 text-body text-ink-3">{description}</p>}
      </div>
      {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * 工具条（CR-082）：左边是「本工地 / 我的」和筛选胶囊，右边是一行汇总。**不是卡片**。
 *
 * `summary` 是汇总那一行（几条、几个人、多少钱，数字取服务端聚合）。
 * `below` 是第二行。窄屏上汇总换到下一行。
 */
export function FilterBar({
  children,
  summary,
  below,
}: {
  children?: ReactNode;
  summary?: ReactNode;
  below?: ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="flex flex-wrap items-center gap-2">
        {children}
        {summary && (
          <div className="ml-auto min-w-0 text-caption text-ink-3 [&_b]:font-semibold [&_b]:text-ink">{summary}</div>
        )}
      </div>
      {below && <div className="mt-3">{below}</div>}
    </div>
  );
}

/**
 * 表单底部的按钮：左「取消」右「提交」，等宽并排，主动作永远在右边。
 */
export function FormActions({
  submitLabel,
  cancelLabel,
  onCancel,
  loading,
  disabled,
  className,
}: {
  submitLabel: ReactNode;
  cancelLabel: ReactNode;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-2 gap-3', className)}>
      <Button type="button" variant="secondary" block onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button type="submit" block loading={loading} disabled={disabled}>
        {submitLabel}
      </Button>
    </div>
  );
}

/**
 * 表格（CR-035，CR-082 重做）。
 *
 * **为什么是表格不是 grid**：一张表只有一套列宽，浏览器自己保证每一行对齐（CR-032 就是 grid 拼歪的）。
 * `table-fixed`：列宽由表头写死 —— 每列给宽度，只留一列不写、吃掉剩下的空间并允许截断。
 *
 * **放不下就根本不显示它**（CR-036）：`xl`（1280px）以上是表格，再窄走各页的 `ListGroup` 列表行 ——
 * 客户原话「不要左右滑动」。
 *
 * 点一行打开详情用 `TableRow`（CR-082），不再往下展开。
 */
export interface DataColumn {
  key: string;
  label?: ReactNode;
  align?: 'left' | 'right';
  /** 列宽，写 Tailwind 的宽度类（`w-24`）。不写就吃掉剩下的宽度 */
  className?: string;
}

export function DataTable({
  columns,
  minWidth = '48rem',
  className,
  children,
}: {
  columns: DataColumn[];
  /** 低于这个宽度就横向滚动，滚的是表格不是页面 */
  minWidth?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card className={cn('hidden overflow-x-auto xl:block', className)}>
      <table
        className={cn(
          'w-full table-fixed border-separate border-spacing-0 text-body',
          '[&_tbody_td]:h-row [&_tbody_td]:border-b [&_tbody_td]:border-rule [&_tbody_td]:px-3 [&_tbody_td]:py-1.5',
          '[&_tbody_tr:last-child_td]:border-b-0',
          '[&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:first-child]:pl-4 [&_th:last-child]:pr-4',
        )}
        style={{ minWidth }}
      >
        <thead>
          <tr className="text-left text-caption text-ink-3">
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  'h-9 whitespace-nowrap border-b border-rule px-3 font-medium',
                  column.align === 'right' && 'text-right',
                  column.className,
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Card>
  );
}

/**
 * 点一下打开详情的表格行（CR-082）。当前打开的那条高亮；键盘回车 / 空格一样。
 * **行上的按钮要自己 `stopPropagation`**（批准、通过），否则点按钮也会打开详情 —— 用 `RowActions` 包一下就行。
 */
export function TableRow({
  selected,
  onOpen,
  className,
  children,
}: {
  selected?: boolean;
  onOpen: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <tr
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'cursor-pointer transition-colors [&>td]:transition-colors',
        selected ? '[&>td]:bg-brand-50' : 'hover:[&>td]:bg-surface-2/60',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/** 表格行尾的动作按钮：右对齐、不换行，点它们不会触发整行打开 */
export function RowActions({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex items-center justify-end gap-1.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * 列表（CR-082）：手机和窄屏上代替表格。一组白底圆角，行与行之间一条从文字开始的线（iOS 设置页的样子）。
 * `title` 是组名（「9/22 周二」），`action` 是组名右边的一个链接。
 */
export function ListGroup({
  title,
  action,
  className,
  children,
}: {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      {(title || action) && (
        <div className="mb-1.5 flex items-baseline justify-between gap-3 px-1 text-caption text-ink-3">
          <span>{title}</span>
          {action}
        </div>
      )}
      {/* 行与行之间的线从文字开始（左边空 1rem），不是通栏 —— 一组读起来是一块 */}
      <div className="overflow-hidden rounded-xl border border-rule bg-surface [&>*]:relative [&>*+*]:before:absolute [&>*+*]:before:left-4 [&>*+*]:before:right-0 [&>*+*]:before:top-0 [&>*+*]:before:h-px [&>*+*]:before:bg-rule">
        {children}
      </div>
    </section>
  );
}

/**
 * 列表的一行：主标题 + 灰色第二行（+ 两行摘要），右边一个值（金额、时间）和状态；左边可以放一张缩略图。
 * 传 `onClick` 或 `to` 就是可点的（点一下打开详情 / 进下一页）。
 */
export function ListRow({
  title,
  subtitle,
  excerpt,
  trailing,
  leading,
  onClick,
  to,
  selected,
  entry,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** 正文摘要，最多两行 */
  excerpt?: ReactNode;
  trailing?: ReactNode;
  leading?: ReactNode;
  onClick?: () => void;
  to?: string;
  selected?: boolean;
  entry?: string;
  className?: string;
}) {
  const body = (
    <>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium text-ink">{title}</span>
        {subtitle && <span className="mt-0.5 block truncate text-caption text-ink-3">{subtitle}</span>}
        {excerpt && <span className="mt-1 line-clamp-2 text-body leading-snug text-ink-2">{excerpt}</span>}
      </span>
      {trailing && (
        <span className="flex shrink-0 flex-col items-end gap-1 self-start pt-0.5 text-right text-caption text-ink-3">
          {trailing}
        </span>
      )}
    </>
  );
  const classes = cn(
    'flex min-h-row w-full items-center gap-3 px-4 py-2.5 text-left',
    (onClick || to) && 'cursor-pointer transition-colors hover:bg-surface-2/60 active:bg-surface-2',
    selected && 'bg-brand-50',
    className,
  );
  if (to) {
    return (
      <Link to={to} data-entry={entry} className={classes}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-entry={entry} className={classes}>
        {body}
      </button>
    );
  }
  return <div className={classes}>{body}</div>;
}

/**
 * 一条统计（CR-082）：格与格之间一条线；传了 `to` 的整格是链接。
 * 数字取服务端聚合，不数当前列表（CR-031）。看不了的传 null，整格不出现（CR-039）。
 */
export interface StatItem {
  key: string;
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  to?: string;
  entry?: string;
  tone?: 'default' | 'danger';
}

export function StatStrip({ items, className }: { items: StatItem[]; className?: string }) {
  const cols = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-2 lg:grid-cols-4' }[
    Math.min(4, Math.max(1, items.length)) as 1 | 2 | 3 | 4
  ];
  return (
    <Card className={cn('grid overflow-hidden', cols, className)}>
      {items.map((item) => {
        const body = (
          <>
            <span className="block text-caption text-ink-3">{item.label}</span>
            <span
              className={cn(
                'mt-0.5 block text-figure font-semibold leading-tight tracking-tight tabular-nums',
                item.tone === 'danger' ? 'text-danger' : 'text-ink',
              )}
            >
              {item.value}
            </span>
            {item.hint && <span className="mt-0.5 block truncate text-caption text-ink-3">{item.hint}</span>}
          </>
        );
        const cell = 'min-w-0 border-rule px-4 py-3.5 [&:not(:first-child)]:border-l max-lg:[&:nth-child(odd)]:border-l-0 max-lg:[&:nth-child(n+3)]:border-t';
        return item.to ? (
          <Link key={item.key} to={item.to} data-entry={item.entry} className={cn(cell, 'block transition-colors hover:bg-surface-2/60')}>
            {body}
          </Link>
        ) : (
          <div key={item.key} className={cell}>
            {body}
          </div>
        );
      })}
    </Card>
  );
}

/** 详情里的「字段名 · 值」两列（CR-082） */
export function DetailList({
  items,
}: {
  /** 条件不成立的项传 null / false / undefined / ''，直接跳过 */
  items: ReadonlyArray<readonly [ReactNode, ReactNode] | null | false | undefined | ''>;
}) {
  return (
    <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-body">
      {items.filter(Boolean).map((item, index) => {
        const [label, value] = item as readonly [ReactNode, ReactNode];
        return (
          <div key={index} className="contents">
            <dt className="text-ink-3">{label}</dt>
            <dd className="min-w-0 break-words text-ink">{value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** 详情里的一段：小标题 + 内容 */
export function DetailSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-caption font-semibold text-ink-3">{title}</h3>
      {children}
    </section>
  );
}

/** 进度时间线（CR-082）：采购「已提交 → 已批准 → 已报销」。点的颜色表示这一步的状态 */
export function Timeline({
  items,
}: {
  items: ReadonlyArray<{ key: string; title: ReactNode; meta?: ReactNode; tone?: 'done' | 'current' | 'bad' | 'todo' }>;
}) {
  const dot = { done: 'bg-ok', current: 'bg-safety-500', bad: 'bg-danger', todo: 'bg-rule-strong' };
  return (
    <ol className="flex flex-col">
      {items.map((item, index) => (
        <li key={item.key} className="relative pb-3 pl-5 text-body last:pb-0">
          <span className={cn('absolute left-0.5 top-1.5 size-2 rounded-full', dot[item.tone ?? 'todo'])} />
          {index < items.length - 1 && <span className="absolute bottom-0 left-[6px] top-4 w-px bg-rule" />}
          <span className="block text-ink">{item.title}</span>
          {item.meta && <span className="block text-caption text-ink-3">{item.meta}</span>}
        </li>
      ))}
    </ol>
  );
}

/** 小标签：列表里的「补录」「补传」「系统」（CR-082）。比徽章小一号、方一点 */
export function Tag({ tone = 'warn', children }: { tone?: 'warn' | 'neutral' | 'brand'; children: ReactNode }) {
  const tones = { warn: 'bg-safety-50 text-safety-600', neutral: 'bg-surface-2 text-ink-3', brand: 'bg-brand-50 text-brand-700' };
  return (
    <span className={cn('inline-flex h-[18px] items-center whitespace-nowrap rounded px-1.5 align-[1px] text-micro font-semibold', tones[tone])}>
      {children}
    </span>
  );
}

/**
 * 分段开关：一组互斥的「看法」（本工地 / 我的、按周 / 按天、列表 / 照片）。灰槽里一块白色滑块。
 * 和 `StatusTabs` 故意长得不一样：页签是**要处理的状态**，这个只换**同一批数据的看法**。
 * `block` 撑满一行（手机上「本工地 / 我的」）。
 */
export function Segmented<T extends string>({
  label,
  hideLabel,
  options,
  value,
  onChange,
  block,
}: {
  /** 这组开关管什么，显示在左边，读屏也念它 */
  label: string;
  /** 旁边已经有标题了（比如「外观」那一行）就不再显示，读屏照念 */
  hideLabel?: boolean;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
  block?: boolean;
}) {
  return (
    <div className={cn('flex items-center gap-2', block && 'w-full')}>
      {!hideLabel && <span className="text-body text-ink-3">{label}</span>}
      <div
        role="group"
        aria-label={label}
        className={cn('inline-flex rounded-lg border border-rule bg-surface-2 p-0.5', block && 'flex w-full')}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                'min-h-touch-sm whitespace-nowrap rounded-md px-2.5 text-body font-medium transition-colors',
                block && 'flex-1',
                selected ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'brand';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-surface-2 text-ink-2',
    ok: 'bg-ok/10 text-ok',
    warn: 'bg-safety-50 text-safety-600',
    danger: 'bg-danger/10 text-danger',
    // 采购的「待报销」要和「已报销」分得开：一个还欠着钱，一个结清了（CR-033）
    brand: 'bg-brand-50 text-brand-700',
  };
  // 胶囊 + 前面一个同色圆点（CR-081）：颜色之外多一个形状，扫一眼一列状态更快分得开
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-medium before:size-1.5 before:rounded-full before:bg-current ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * 背景滚动锁的引用计数。
 *
 * 改之前每个抽屉各自保存 / 还原 `body.style.overflow` 的单个值，
 * 两层嵌套时只有「内层先关」这一种顺序恰好正确 —— 外层先关的话
 * 它会把 body 还原成 ''，而内层还开着，背景就能滚了。
 * 计数器省掉以后每次叠层都要重新推理一遍。
 */
let scrollLocks = 0;
let scrollLockPrevious = '';

/**
 * 锁住背景滚动，返回解锁函数。
 *
 * 导出是为了给全屏图纸查看器用 —— 它不是 Drawer，但同样需要「背后别跟着滚」。
 * 让它自己写一份的话就有了第二个计数器，两边各数各的，叠起来时必然错一次。
 */
export function lockScroll(): () => void {
  if (scrollLocks === 0) {
    scrollLockPrevious = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
  return () => {
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = scrollLockPrevious;
  };
}

/**
 * 详情面板（CR-082，原来的侧滑抽屉）。
 *
 * **电脑从右侧滑出**：离四边 8px、圆角、列表原地不动 —— 看一条的时候还看得见它在列表里的哪儿。
 * **手机从底部弹出**：顶上留一截、圆角、顶上一条小横杠。
 * 详情和表单（新建账号、记一笔、补录打卡）都放在这里。`footer` 是底部的动作按钮（右对齐、主动作在最右）。
 *
 * `size`：md（540px）/ wide（768px，放一张表）。`layer`：默认 `drawer`；要**被另一层盖住**时用 `sheet`（低一档），
 * 叠两层时关掉内层回到外层。
 *
 * 焦点管理刻意做得简单：打开时焦点进面板，Esc 关闭。
 */
const drawerPanel = cva(
  [
    'safe-bottom relative flex w-full flex-col overflow-hidden bg-surface text-ink shadow-pop outline-none',
    // 电脑：右侧浮起的一块
    'md:my-2 md:mr-2 md:h-[calc(100dvh-1rem)] md:rounded-2xl md:border md:border-rule',
    // 手机：底部弹出，顶上留出 3rem 看得见后面是什么
    'max-md:mt-12 max-md:h-[calc(100dvh-3rem)] max-md:rounded-t-2xl',
  ],
  {
    variants: {
      size: { md: 'md:max-w-[540px]', wide: 'md:max-w-3xl' },
    },
    defaultVariants: { size: 'md' },
  },
);

export function Drawer({
  open,
  title,
  subtitle,
  footer,
  onClose,
  size,
  layer = 'drawer',
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  layer?: 'drawer' | 'sheet';
  children: ReactNode;
} & VariantProps<typeof drawerPanel>) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  // onClose 几乎总是行内箭头函数，每次渲染都是新的。放进 ref，让下面的 effect 只跟着 open 走 ——
  // 改之前它依赖 [open, onClose]：父组件每敲一个键重渲染一次，effect 就重跑一次，
  // 里面的 focus() 把焦点从输入框抢到面板上，下一个键打空。客户在图纸加图钉时实测「打字丢失」
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    // 只在打开那一刻聚焦一次，之后焦点归输入框。里面已经有控件抢到焦点（autoFocus）就不抢回来
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    // 开着时锁住背后的滚动，否则滚轮会滚外面的列表
    const unlock = lockScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 flex justify-end print:hidden max-md:items-end',
        layer === 'sheet' ? 'z-sheet' : 'z-drawer',
      )}
    >
      <button
        type="button"
        aria-label={t('app.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/25 md:bg-black/15"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // 抽屉自己滚，里面贴底的保存栏贴抽屉的底，不用让开 App 的底部 Tab
        className={cn(drawerPanel({ size }), '[--sticky-bottom:0px]')}
      >
        {/* 手机上顶上一条小横杠：看得出这是一张能关掉的卡片 */}
        <span aria-hidden className="mx-auto mt-1.5 h-1 w-9 shrink-0 rounded-full bg-rule-strong md:hidden" />
        <div className="flex shrink-0 items-start gap-3 border-b border-rule px-5 py-3.5 max-md:px-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-title font-semibold tracking-tight">{title}</h2>
            {subtitle && <div className="mt-0.5 text-caption text-ink-3">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 inline-flex min-h-touch-sm shrink-0 items-center gap-1.5 rounded-md px-2 text-body text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            {t('app.close')}
            <kbd className="hidden rounded border border-rule px-1 font-sans text-micro text-ink-4 md:inline">Esc</kbd>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 max-md:px-4 max-md:py-4">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-rule px-5 py-3 max-md:px-4 max-md:[&>*]:flex-1">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
